/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef } from 'react';

import {
  GridFooterContainer,
  GridSelectedRowCount,
  selectedGridRowsCountSelector,
  useGridSelector,
  useGridRootProps,
  gridFilteredTopLevelRowCountSelector,
  gridTopLevelRowCountSelector,
  useGridApiContext,
} from '@mui/x-data-grid';
import { Paper } from '@mui/material';
import { throttle, isNil } from 'lodash';
import clsx from 'clsx';

import { useBreakpoints } from 'helpers/styles';

import s from './styled';

export const DataGridFooter = React.forwardRef(function DataGridFooter(
  props,
  ref,
) {
  const { total, renderBefore, ...rest } = props;
  const rootProps = useGridRootProps();
  const apiRef = useGridApiContext();
  const { columns } = rootProps;

  const totalBlockRef = useRef();
  const hasTotalBlockInRef = !!totalBlockRef.current;

  useEffect(() => {
    const totalBlockElement = totalBlockRef.current;

    if (!totalBlockElement) return;
    let preventEvent = false;

    const onTotalBlockScroll = throttle((event) => {
      if (preventEvent) {
        preventEvent = false;
        return;
      }

      preventEvent = true;

      apiRef.current.scroll({
        left: event.target.scrollLeft,
      });
    }, 10);
    totalBlockElement.addEventListener('scroll', onTotalBlockScroll);

    const unsub = apiRef.current.subscribeEvent(
      'rowsScroll',
      throttle((coords) => {
        if (preventEvent) {
          preventEvent = false;
          return;
        }

        preventEvent = true;
        if (totalBlockRef.current) {
          totalBlockRef.current.scrollLeft = coords.left;
        }
      }, 10),
    );

    return () => {
      unsub();
      totalBlockElement.removeEventListener('scroll', onTotalBlockScroll);
    };
  }, [apiRef, hasTotalBlockInRef]);

  /**
   * MUI code
   */

  const totalTopLevelRowCount = useGridSelector(
    apiRef,
    gridTopLevelRowCountSelector,
  );
  const selectedRowCount = useGridSelector(
    apiRef,
    selectedGridRowsCountSelector,
  );
  const visibleTopLevelRowCount = useGridSelector(
    apiRef,
    gridFilteredTopLevelRowCountSelector,
  );

  const selectedRowCountElement =
    !rootProps?.hideFooter &&
    !rootProps?.hideFooterSelectedRowCount &&
    selectedRowCount > 0 ? (
      <GridSelectedRowCount selectedRowCount={selectedRowCount} />
    ) : null;

  const rowCountElement =
    !rootProps?.hideFooter &&
    !rootProps.hideFooterRowCount &&
    !rootProps.pagination ? (
      <rootProps.slots.footerRowCount
        {...rootProps.slotProps?.footerRowCount}
        rowCount={totalTopLevelRowCount}
        visibleRowCount={visibleTopLevelRowCount}
      />
    ) : null;

  const paginationElement =
    !rootProps.hideFooter &&
    !rootProps.hideFooterPagination &&
    rootProps.pagination &&
    rootProps.slots?.pagination ? (
      <rootProps.slots.pagination {...rootProps.slotProps?.pagination} />
    ) : null;

  /**
   * End of MUI code
   */

  const { isSmUp } = useBreakpoints();

  const totalArray = useMemo(() => {
    if (!total) return;

    const result = [];
    const totalLabel = {
      field: 'totalLabel',
      value: total.totalLabel,
      width: 0,
    };
    let passTotalLabel = false;

    columns.forEach((colDef) => {
      const inTotal = !!total[colDef.field] || total[colDef.field] === 0;
      if (!inTotal && !passTotalLabel) {
        totalLabel.width += colDef.width;

        return;
      }

      if (inTotal && !result.length) {
        passTotalLabel = true;

        result.push(totalLabel);
      }

      if (passTotalLabel) {
        const value = (total[colDef.field] ?? '').toString();

        result.push({
          ...colDef,
          value:
            !isNil(value) && value !== '' && !!colDef.valueFormatter
              ? colDef.valueFormatter(value, {
                  field: colDef.field,
                  api: apiRef.current,
                })
              : value,
        });
      }
    });

    return result;
  }, [total, columns, apiRef]);

  return (
    <>
      {!!totalArray?.length && (
        <s.TotalBlock
          component={Paper}
          className="DataGridFooter-total"
          ref={totalBlockRef}
        >
          {isSmUp && (
            <div className="MuiDataGrid-row">
              {totalArray?.map((item) => {
                return (
                  <s.TotalTableCell
                    key={item.field}
                    style={{
                      width: item.width,
                      height: rootProps.rowHeight,
                      lineHeight: `${rootProps.rowHeight}px`,
                    }}
                    className={clsx('MuiDataGrid-cell', {
                      'MuiDataGrid-cell-totalLabel':
                        item.field === 'totalLabel',
                      'MuiDataGrid-cell--textLeft': item.align === 'left',
                      'MuiDataGrid-cell--textCenter': item.align === 'center',
                      'MuiDataGrid-cell--textRight': item.align === 'right',
                    })}
                  >
                    {item.value}
                  </s.TotalTableCell>
                );
              })}
            </div>
          )}
          {!isSmUp && (
            <>
              {totalArray[0].field === 'totalLabel' && (
                <s.TotalRow $simple>
                  <s.TotalTableLabel $simple>
                    {totalArray[0].value}
                  </s.TotalTableLabel>
                </s.TotalRow>
              )}

              {totalArray?.map((item) => {
                if (!item.headerName || isNil(item.value)) return null;

                return (
                  <s.TotalRow key={item.field}>
                    <s.TotalTableLabel>{item.headerName}</s.TotalTableLabel>

                    <s.TotalTableValue>{item.value}</s.TotalTableValue>
                  </s.TotalRow>
                );
              })}
            </>
          )}
        </s.TotalBlock>
      )}

      {!rootProps?.hideFooter && (
        <GridFooterContainer ref={ref} {...rest}>
          {renderBefore?.(rootProps)}
          {selectedRowCountElement}
          {rowCountElement}
          {paginationElement}
        </GridFooterContainer>
      )}
    </>
  );
});
